import React, { useState, useEffect, useRef } from 'react';
import OrderLedgerBuilder from './OrderLedgerBuilder';
import PositionLedgerSummary from './PositionLedgerSummary';
import PositionLedgerTable from './PositionLedgerTable';
import './PositionLedger.css';

// ─── Dashboard palette (used only for dynamic/data-driven values) ─────────────
const C = {
  bg:     '#08101e',
  bg1:    '#0c1525',
  bg2:    '#101b2e',
  border: '#1e2d42',
  border2:'#243548',
  text:   '#e8edf5',
  body:   '#b8c8dc',
  meta:   '#6a7d95',
  green:  '#00e896',
  cyan:   '#00c8ff',
  purple: '#9966ff',
  font:   "'DM Mono', ui-monospace, monospace",
};

// Per-tab config — accent is the only dynamic value needed inline
const TABS = [
  { key: 'stocks',  label: 'Stocks',  accent: C.green,  countColor: C.green  },
  { key: 'options', label: 'Options', accent: C.purple, countColor: C.purple },
];

function PositionLedger({ rawOrders, ledgerView: ledgerViewProp, proceedEvals = [], onOptionClick }) {
  const [ledgerViewInternal, setLedgerViewInternal] = useState({ stocks: [], options: [], summary: {} });
  const [isLoading,  setIsLoading]  = useState(true);
  const [activeTab,  setActiveTab]  = useState('stocks');
  const ledgerBuilderRef = useRef(new OrderLedgerBuilder());

  // If pre-computed ledgerView is passed in from TradingCommand, use it directly
  // Otherwise fall back to building internally from rawOrders
  const ledgerView = ledgerViewProp || ledgerViewInternal;

  useEffect(() => {
    // Skip internal build if parent is supplying ledger data
    if (ledgerViewProp) { setIsLoading(false); return; }
    if (!rawOrders || rawOrders.length === 0) { setIsLoading(false); return; }
    try {
      const view = ledgerBuilderRef.current.processOrderHistory(rawOrders);
      setLedgerViewInternal(view);
    } catch (err) {
      console.error('❌ PositionLedger build error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [rawOrders, ledgerViewProp]);

  if (isLoading) {
    return (
      <div className="pl-empty" style={{ textAlign: 'center', padding: '32px' }}>
        <div style={{ fontFamily: C.font, fontSize: 10, color: C.meta }}>
          Loading positions…
        </div>
      </div>
    );
  }

  const activeStocks  = ledgerView.summary.active_stocks  || 0;
  const closedStocks  = ledgerView.summary.closed_stocks  || 0;
  const activeOptions = ledgerView.summary.active_options || 0;
  const closedOptions = ledgerView.summary.closed_options || 0;

  const tabCounts = {
    stocks:  { active: activeStocks,  closed: closedStocks  },
    options: { active: activeOptions, closed: closedOptions },
  };

  return (
    <div className="position-ledger">

      {/* Summary strip */}
      <PositionLedgerSummary summary={ledgerView.summary} />

      {/* Card wrapper — static styles from CSS */}
      <div className="ledger-card">

        {/* Tab bar — static layout from CSS, dynamic accent inline */}
        <div className="ledger-tab-bar">
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const counts   = tabCounts[tab.key];
            return (
              <button
                key={tab.key}
                className="ledger-tab"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  // Only the dynamic values that CSS can't know:
                  color:       isActive ? C.text  : C.meta,
                  borderBottom: isActive ? `2px solid ${tab.accent}` : '2px solid transparent',
                  background:  isActive ? C.bg2   : 'transparent',
                }}
              >
                {tab.label}
                {/* Active count badge */}
                {counts.active > 0 && (
                  <span className="ledger-tab-count" style={{
                    color:       tab.accent,
                    background:  `${tab.accent}18`,
                    borderColor: `${tab.accent}50`,
                  }}>
                    {counts.active}
                  </span>
                )}
                {/* Closed count badge */}
                {counts.closed > 0 && (
                  <span className="ledger-tab-count" style={{
                    color:       C.meta,
                    background:  `${C.meta}12`,
                    borderColor: `${C.meta}40`,
                  }}>
                    {counts.closed}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable content */}
        <div className="ledger-scroll-area">
          {activeTab === 'stocks' && (
            <PositionLedgerTable
              positions={ledgerView.stocks}
              assetClass="stock"
            />
          )}
          {activeTab === 'options' && (
            <PositionLedgerTable
              positions={ledgerView.options}
              assetClass="option"
              proceedEvals={proceedEvals}
              onOptionClick={onOptionClick}
            />
          )}
        </div>

      </div>
    </div>
  );
}

export default PositionLedger;